import React, { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiCopy,
  FiCheck,
  FiEdit3,
  FiCalendar,
  FiUser,
  FiFolder,
} from "react-icons/fi";
import "./NoteViewerModal.css";

const NoteViewerModal = ({
  isOpen,
  onClose,
  title,
  content,
  createdBy,
  createdAt,
  folder,
  tags = [],
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      const textToCopy = `${title}\n\n${content}`;
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
// Fallback
      const textArea = document.createElement("textarea");
      textArea.value = `${title}\n\n${content}`;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="note-viewer-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="note-viewer-modal"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <header className="note-viewer-header">
            <div className="note-viewer-title-section">
              <div className="note-viewer-icon">
                <FiEdit3 size={20} />
              </div>
              <div className="note-viewer-title-info">
                <h2 className="note-viewer-title">{title}</h2>
                <div className="note-viewer-meta">
                  {createdBy && (
                    <span className="note-viewer-author">
                      <FiUser size={12} />
                      {createdBy.name || createdBy}
                    </span>
                  )}
                  {createdAt && (
                    <span className="note-viewer-date">
                      <FiCalendar size={12} />
                      {formatDate(createdAt)}
                    </span>
                  )}
                  {folder && folder !== "General" && (
                    <span className="note-viewer-folder">
                      <FiFolder size={12} />
                      {folder}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="note-viewer-actions">
              <button
                className={`note-viewer-btn ${copied ? "note-viewer-btn--success" : "note-viewer-btn--primary"}`}
                onClick={handleCopy}
              >
                {copied ? <FiCheck size={16} /> : <FiCopy size={16} />}
                <span>{copied ? "Copied!" : "Copy"}</span>
              </button>
              <button className="note-viewer-close" onClick={onClose}>
                <FiX size={20} />
              </button>
            </div>
          </header>

          {/* Content */}
          <div className="note-viewer-content">
            <div className="note-viewer-text">
              {content?.split("\n").map((paragraph, idx) => (
                <p key={idx}>{paragraph || <br />}</p>
              ))}
            </div>
          </div>

          {/* Tags Footer */}
          {tags && tags.length > 0 && (
            <footer className="note-viewer-footer">
              <div className="note-viewer-tags">
                {tags.map((tag, idx) => (
                  <span key={idx} className="note-viewer-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </footer>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default NoteViewerModal;
